import { StripeFlags } from "../resources/modelTypes";

const usDollarFormatter = new Intl.NumberFormat("en-US", {
  currency: "USD",
  maximumFractionDigits: 0,
  style: "currency",
});

export const formatDollars = (cents: number) =>
  usDollarFormatter.format(cents / 100);

export const isStripeGoalMet = (fundedCents: number, goalAmountCents: number) =>
  goalAmountCents > 0 && fundedCents >= goalAmountCents;

export const QUICK_OPTION_DOLLARS = [50, 100, 200, 500, 1_000];

/**
 * Query param letting a host-facing email deep-link into a Stripe modal. Read
 * and stripped by `HostStripeProvider`; kept in sync by hand with
 * api/hitchedup/stripe_registry/webhook_views.py.
 */
export const STRIPE_ACTION_PARAM = "stripeAction";

/** Value of {@link STRIPE_ACTION_PARAM} that opens the received-gifts modal. */
export const STRIPE_ACTION_ADD_PAYOUT_INFO = "addPayoutInfo";

/** Which notice the host needs about their Stripe connected account. */
export enum StripeAlertVariant {
  /** Onboarding isn't finished and Stripe is waiting on the host. */
  CompleteSetup = "completeSetup",
  /** Gifts are fine, but payouts are held pending more info from the host. */
  UpdateAccountInfo = "updateAccountInfo",
  /** Something is blocked, but Stripe is reviewing rather than waiting. */
  Verifying = "verifying",
}

/**
 * Pick the alert variant for a host's Stripe readiness flags, or `null` when
 * the account is ready. Call sites use the `null` case to skip rendering their
 * alert wrapper, so an unneeded notice leaves no empty slot behind.
 *
 * @param stripeFlags - Webhook-derived readiness flags for the host's account.
 */
export const getStripeAlertVariant = (
  stripeFlags: StripeFlags,
): StripeAlertVariant | null => {
  const { awaitingHostAction, giftsAllowed, payoutsAllowed } = stripeFlags;

  if (giftsAllowed && payoutsAllowed) {
    return null;
  }
  // Stripe is reviewing what it already has, so there's nothing to ask for.
  if (!awaitingHostAction) {
    return StripeAlertVariant.Verifying;
  }
  // Gifts already clear, so it's payouts the missing info is holding up.
  if (giftsAllowed) {
    return StripeAlertVariant.UpdateAccountInfo;
  }
  return StripeAlertVariant.CompleteSetup;
};
